import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { Button } from '@/components/ui/button';
import { MAX_CHARS } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useSocket } from '@/hooks/useSocket';
import { afterSurveyAction, chatModalState, isMobileState, isWidgetExpandedState, } from '@/state/session';
export const Survey = () => {
    const [activeButton, setActiveButton] = useState(null);
    const isMobile = useRecoilValue(isMobileState);
    const isExpanded = useRecoilValue(isWidgetExpandedState);
    const { trackEvent } = useAnalytics();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [hoveredButton, setHoveredButton] = useState(null);
    const surveyTexts = [
        t('survey.terrible'),
        t('survey.bad'),
        t('survey.okay'),
        t('survey.good'),
        t('survey.excellent'),
    ];
    const { sendRating } = useSocket();
    const setChatModal = useSetRecoilState(chatModalState);
    const setAfterSurveyAction = useSetRecoilState(afterSurveyAction);
    const inputRef = useRef(null);
    const inputContainerRef = useRef(null);
    const [input, setInput] = useState('');
    const afterSurveyActionState = useRecoilValue(afterSurveyAction);
    const handleClose = useCallback(async () => {
        setChatModal(null);
        // Execute the stored action after closing the survey modal
        if (afterSurveyActionState) {
            afterSurveyActionState();
            // Clear the action after execution
            setAfterSurveyAction(null);
        }
    }, [afterSurveyActionState, setChatModal, setAfterSurveyAction]);
    useEffect(() => {
        if (inputRef.current) {
            setTimeout(() => {
                var _a;
                (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 300);
        }
    }, [input]);
    const handleLimitInputChange = useCallback(() => {
        var _a, _b, _c;
        if (((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) &&
            ((_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.textContent.length) >= MAX_CHARS) {
            inputRef.current.textContent = (_c = inputRef.current) === null || _c === void 0 ? void 0 : _c.textContent.slice(0, MAX_CHARS);
            const selection = window.getSelection();
            const range = document.createRange();
            selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
            range.selectNodeContents(inputRef.current);
            range.collapse(false);
            selection === null || selection === void 0 ? void 0 : selection.addRange(range);
            inputRef.current.focus();
        }
        const element = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
        if (!(element === null || element === void 0 ? void 0 : element.textContent)) {
            const breakTag = element === null || element === void 0 ? void 0 : element.getElementsByTagName('br')[0];
            if (breakTag) {
                element === null || element === void 0 ? void 0 : element.removeChild(breakTag);
            }
        }
    }, []);
    const handleInputChange = useCallback(() => {
        var _a;
        handleLimitInputChange();
        setInput(((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) || '');
    }, [handleLimitInputChange]);
    const handleSubmit = useCallback(() => {
        if (activeButton !== null) {
            trackEvent(AnalyticsEvent.RatingSubmitted);
            setIsLoading(true);
            sendRating({ rating: activeButton + 1, feedback: input });
            setChatModal(null);
            // Note: The afterSurveyAction will be executed when the 'rated' event is received
        }
    }, [activeButton, input, sendRating, setChatModal, trackEvent]);
    const handleRatingClick = (index) => {
        // Track review popover interaction when a rating is clicked
        trackEvent(AnalyticsEvent.RatingClicked, { rating: index + 1 });
        setActiveButton(index);
    };
    const handleSkipAndClose = useCallback(() => {
        trackEvent(AnalyticsEvent.PopoverReviewSkipAndCloseClicked);
        handleClose();
    }, [handleClose, trackEvent]);
    return (_jsxs("div", { className: "flex h-full flex-col items-center text-center", children: [_jsx("p", { className: "text-text-secondary-foreground mt-4 text-center font-semibold leading-none", children: t('survey.experience') }), _jsx("div", { className: "group mx-5 mb-4 mt-4 grid grid-cols-5 p-0", onMouseLeave: () => setHoveredButton(null), children: Array(5)
                    .fill('')
                    .map((_, index) => {
                    const isActive = activeButton === index;
                    const isHovered = hoveredButton === index;
                    const showLabel = isActive ||
                        isHovered ||
                        ((index === 0 || index === 4) &&
                            activeButton === null &&
                            hoveredButton === null);
                    return (_jsxs("div", { className: "flex h-[82px] flex-col items-center justify-center", onMouseEnter: () => setHoveredButton(index), children: [_jsx("button", { className: cn('flex cursor-pointer items-center justify-center rounded-full border-0 text-lg font-medium transition-all duration-300', isActive || isHovered
                                    ? 'bg-(--color-rating-background) h-[42px] w-[42px] text-primary-foreground'
                                    : 'h-10 w-10 bg-muted text-foreground'), onClick: () => handleRatingClick(index), "aria-label": String(index + 1), children: index + 1 }), _jsx("span", { className: cn('mt-2 text-xs text-muted-foreground transition-opacity duration-300', showLabel ? 'opacity-100' : 'opacity-0'), children: surveyTexts[index] })] }, index));
                }) }), _jsx("hr", { className: "w-full border-border" }), _jsx("div", { ref: inputContainerRef, className: cn('flex h-[72px] w-[328px] grow flex-col-reverse', {
                    'w-sm': isExpanded,
                }), children: _jsx("div", { ref: inputRef, className: cn('wrap-break-word mb-6 h-[72px] w-full appearance-none self-start overflow-y-auto text-left text-base text-foreground will-change-transform', {
                        'text-base': isMobile,
                    }), "aria-label": t('survey.input_placeholder'), role: "textbox", tabIndex: 0, "data-placeholder": t('survey.input_placeholder'), contentEditable: true, onInput: handleInputChange }) }), _jsxs("div", { className: "mb-2 flex flex-col items-center justify-center gap-2", children: [_jsx(Button, { disabled: activeButton === null, onClick: handleSubmit, className: cn('w-[328px]', {
                            'w-sm': isExpanded,
                        }), isLoading: isLoading, children: t('buttons.submit') }), _jsx(Button, { onClick: handleSkipAndClose, variant: "ghost-neutral", className: cn('text-(--color-link) w-[328px]', {
                            'w-sm': isExpanded,
                        }), children: t('buttons.skip') })] })] }));
};
